import React, { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
// @mui material components
// Material Dashboard 2 React components
import MDBox from "components/MDBox";
// Data
import MDInput from "../MDInput";
import Grid from "@mui/material/Grid";
import HomeworkTypeSelect from "../HomeworkTypeSelect";
import HomeworkCategorySelect from "../HomeworkCategorySelect";
import InputLabel from "@mui/material/InputLabel";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import FormControl from "@mui/material/FormControl";
import MDTypography from "../MDTypography";
import Tab from "@mui/material/Tab";
import Icon from "@mui/material/Icon";
import Tabs from "@mui/material/Tabs";
import MDButton from "../MDButton";
import IconButton from "@mui/material/IconButton";
import DeleteIcon from "@mui/icons-material/Delete";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import MilestoneEdition from "./MilestoneEdition";
import activities from "../activities";

const MILESTONES_LIMIT = 10;

function CreateHomework({ homework = {}, onChange }) {
  const [tab, setTab] = useState(0);
  const [milestoneEdition, setMilestoneEdition] = useState(null);

  const milestones = homework.milestones || [];
  const handleAddMilestone = useMemo(
    () => () => {
      if (milestones.length < MILESTONES_LIMIT) {
        onChange({
          ...homework,
          milestones: milestones.concat([
            {
              id: "tmp-" + (Math.floor(Math.random() * 100000) + 10000),
              validation_mode: "parent",
              validation_params: { validationMark: 75 },
            },
          ]),
        });
      }
    },
    [homework, milestones]
  );
  const handleRemoveMilestone = useMemo(
    () => (id) => {
      onChange({
        ...homework,
        milestones: milestones.filter((m) => m.id !== id),
      });
    },
    [homework, milestones]
  );
  const handleDuplicateMilestone = useMemo(
    () => (milestone, i) => {
      const newMilestones = milestones.slice();
      newMilestones.splice(i + 1, 0, {
        ...milestone,
        id: "tmp-" + (Math.floor(Math.random() * 100000) + 10000),
      });
      onChange({
        ...homework,
        milestones: newMilestones,
      });
      //setMilestoneEdition(i + 1);
    },
    [homework, milestones]
  );

  const handleMilestoneChange = (milestone) => {
    onChange({
      ...homework,
      milestones: milestones.map((m) => (m.id !== milestone.id ? m : milestone)),
    });
  };

  useEffect(() => {
    if (milestones.length === 0) {
      handleAddMilestone();
    }
  }, [milestones.length, handleAddMilestone]);

  return (
    <MDBox>
      <MDBox mb={2}>
        <Tabs value={tab} onChange={(event, newValue) => setTab(newValue)}>
          <Tab
            label="Informations"
            icon={
              <Icon fontSize="small" sx={{ mt: -0.25 }}>
                format_list_bulleted
              </Icon>
            }
          />
          <Tab
            label="Validation"
            icon={
              <Icon fontSize="small" sx={{ mt: -0.25 }}>
                assignment_turned_in
              </Icon>
            }
          />
        </Tabs>
      </MDBox>
      <Grid container spacing={2} sx={{ display: tab === 0 ? "flex" : "none" }}>
        <Grid item xs={12}>
          <MDBox>
            <MDInput
              type="text"
              label="Nom"
              name="name"
              value={homework.name || ""}
              onChange={(e) => onChange({ ...homework, name: e.target.value })}
              fullWidth
            />
          </MDBox>
        </Grid>
        <Grid item xs={12}>
          <MDBox>
            <MDInput
              type="text"
              label="Description"
              name="description"
              multiline
              rows={4}
              value={homework.description || ""}
              onChange={(e) => onChange({ ...homework, description: e.target.value })}
              fullWidth
            />
          </MDBox>
        </Grid>
        <Grid item xs={12} sm={6}>
          <MDBox>
            <HomeworkTypeSelect
              value={homework.type}
              onChange={(type) => onChange({ ...homework, type })}
            />
          </MDBox>
        </Grid>
        <Grid item xs={12} sm={6}>
          <MDBox>
            <HomeworkCategorySelect
              value={homework.category}
              onChange={(category) => onChange({ ...homework, category })}
            />
          </MDBox>
        </Grid>
      </Grid>
      <Grid container spacing={1} sx={{ display: tab === 1 ? "flex" : "none" }}>
        <Grid item xs={12}>
          <MDBox>
            <FormControl fullWidth>
              <InputLabel id="validation-label">Validation</InputLabel>
              <Select
                labelId="validation-label"
                id="validation"
                value={homework.validation}
                label="Validation"
                onChange={(e) => onChange({ ...homework, validation: e.target.value })}
              >
                <MenuItem value={"none"}>Aucune</MenuItem>
                <MenuItem value={"individual"}>Validation individuelle</MenuItem>
                <MenuItem value={"daily"}>Validation journalière</MenuItem>
              </Select>
            </FormControl>
          </MDBox>
          {homework.validation === "none" && (
            <MDTypography variant="caption" sx={{ fontStyle: "italic" }}>
              Aucune validation ne sera requise pour ce devoir.
            </MDTypography>
          )}
          {homework.validation === "individual" && (
            <MDTypography variant="caption" sx={{ fontStyle: "italic" }}>
              Chaque étape pourra être validée séparément, à n&apos;importe quel moment.
            </MDTypography>
          )}
          {homework.validation === "daily" && (
            <MDTypography variant="caption" sx={{ fontStyle: "italic" }}>
              Les étapes de ce devoir devront être validées sur plusieurs jours.
            </MDTypography>
          )}
        </Grid>
        {!!homework.validation && homework.validation !== "none" && (
          <Grid item xs={12}>
            <MDBox sx={{ maxHeight: "45vh", overflow: "auto" }}>
              {milestones.map((milestone, i) => {
                return (
                  <MDBox
                    key={milestone.id}
                    display="flex"
                    alignItems="center"
                    sx={{
                      border: "1px solid rgba(0, 0, 0, 0.25)",
                      borderRadius: 2,
                      padding: 1,
                      mt: 0.5,
                      cursor: "pointer",
                      "&:hover": {
                        backgroundColor: "#f8f9fa",
                      },
                    }}
                    onClick={() => setMilestoneEdition(i)}
                  >
                    <MDBox sx={{ flex: 1 }}>
                      <MDBox>
                        {!milestone.description && "Étape " + (i + 1)}
                        {milestone.description}
                      </MDBox>
                      <MDTypography
                        variant="caption"
                        component="div"
                        sx={{ fontStyle: "italic", lineHeight: "1.5", mt: 0.25 }}
                      >
                        {milestone.validation_mode === "parent" &&
                          "Validation par le parent. Votre enfant pourra vous notifier afin de penser à valider l'étape."}
                        {milestone.validation_mode === "manual" &&
                          "Validation manuelle. L'enfant soumettra son étape lorsque celle-ci sera terminée" +
                            (milestone.validation_params && milestone.validation_params.proofs
                              ? (milestone.validation_params.min > 0
                                  ? ", et devra founir au moins " +
                                    milestone.validation_params.min +
                                    " preuve" +
                                    (milestone.validation_params.min > 1 ? "s" : "") +
                                    " de son travail"
                                  : ", et pourra founir des preuves de son travail") +
                                (milestone.validation_params.max > 0
                                  ? " (maximum " + milestone.validation_params.max + ")"
                                  : "")
                              : "") +
                            "."}
                        {milestone.validation_mode === "autonomous" &&
                          "Validation autonome. Votre enfant pourra valider son étape par lui-même."}
                        {milestone.validation_mode === "activity" &&
                          "Validation automatique à l'aide d'une activité personnalisée" +
                            (milestone.validation_params &&
                            milestone.validation_params.activityType &&
                            activities[milestone.validation_params.activityType]
                              ? " (" +
                                activities[milestone.validation_params.activityType].title +
                                ")"
                              : "") +
                            "."}
                      </MDTypography>
                    </MDBox>
                    <IconButton
                      aria-label="remove"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDuplicateMilestone(milestone, i);
                      }}
                      color="info"
                      disabled={milestones.length >= MILESTONES_LIMIT}
                    >
                      <ContentCopyIcon />
                    </IconButton>
                    <IconButton
                      aria-label="remove"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleRemoveMilestone(milestone.id);
                      }}
                      color="error"
                      disabled={milestones.length < 2}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </MDBox>
                );
              })}
              {milestoneEdition !== null && (
                <MilestoneEdition
                  milestone={milestones[milestoneEdition]}
                  position={milestoneEdition + 1}
                  onChange={handleMilestoneChange}
                  onClose={() => setMilestoneEdition(null)}
                />
              )}
            </MDBox>
            <MDBox display="flex" alignItems="center" justifyContent="center" sx={{ mt: 1 }}>
              <MDButton
                variant="contained"
                color="info"
                onClick={handleAddMilestone}
                disabled={milestones.length >= MILESTONES_LIMIT}
              >
                <Icon fontSize="small" sx={{ mr: 1 }}>
                  add_circle_outline
                </Icon>
                Ajouter une étape
              </MDButton>
            </MDBox>
          </Grid>
        )}
      </Grid>
    </MDBox>
  );
}
CreateHomework.propTypes = {
  homework: PropTypes.object,
  onChange: PropTypes.func.isRequired,
};

export default CreateHomework;
